// Which chat surface the Page drawer mounts. Flags default to false while
// the FeatureFlags query is in flight, and isFullAdmin reads false while the
// user query is, so choosing on either raw input would mount the legacy KB
// chat and then swap to the Opus Agent drawer once it lands — a
// wrong-surface flash for agent users on every load. Until BOTH the flags
// and the user identity are resolved, mount neither.
export type ChatSurface = "opus-agent" | "knowledge-base" | "none";

// The main content only "joins" the right-hand KB drawer (flush right edge)
// while it is actually mounted beside it. The Opus Agent panel sits on the
// left as its own card.
export const isChatJoined = ({
  shouldShowChat,
  chatSurface,
  chatOpen,
}: {
  shouldShowChat: boolean;
  chatSurface: ChatSurface;
  chatOpen: boolean;
}): boolean => shouldShowChat && chatSurface === "knowledge-base" && chatOpen;

export const resolveChatSurface = ({
  flagsResolved,
  userResolved,
  opusAgentEnabled,
  isFullAdmin,
  managerAgentsEnabled = false,
}: {
  flagsResolved: boolean;
  userResolved: boolean;
  opusAgentEnabled: boolean;
  isFullAdmin: boolean;
  // A manager with Ff.OpusAgentManagers: the backend lets them chat with the
  // Primary Agent whatever Ff.OpusAgent says (scadmin
  // opus_agent/controllers/access.py).
  managerAgentsEnabled?: boolean;
}): ChatSurface => {
  if (!flagsResolved || !userResolved) return "none";
  return (opusAgentEnabled && isFullAdmin) || managerAgentsEnabled
    ? "opus-agent"
    : "knowledge-base";
};

export type AgentPanelGates = {
  askOpus: boolean;
  // The org's custom agents: admin-only.
  agents: boolean;
  // The managers' default agents, a prototype until the backend serves them
  // (see managerAgents.ts). Absent = off.
  managerAgents?: boolean;
};

// The left agent panel: Ask Opus for every full admin with Ff.OpusAgent,
// independent of the KB opt-in (which now only governs trainees' and
// managers' KB chat), and the org's agents beside it: they come with
// Ff.OpusAgent too. Managers with Ff.OpusAgentManagers get Ask Opus and the
// prototype manager agents; never the org's custom agents.
// The opus-agent surface keeps the panel and the KB drawer from mounting
// together.
export const resolveAgentPanelGates = ({
  chatSurface,
  allowedOnPage,
  isFullAdmin,
}: {
  chatSurface: ChatSurface;
  allowedOnPage: boolean;
  isFullAdmin: boolean;
}): AgentPanelGates => {
  const askOpus = chatSurface === "opus-agent" && allowedOnPage;
  return {
    askOpus,
    agents: askOpus && isFullAdmin,
    managerAgents: askOpus && !isFullAdmin,
  };
};
